import React, { useState, useEffect } from "react";
import { Fab, Theme } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EndGameIcon from "@material-ui/icons/CheckCircleOutline";

import messages from "./PageViewMatch.intl";
import { styled, useTheme } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { QUERY_MATCH, UPDATE_MATCH_EVENT, END_MATCH } from "./MatchQueries";
import { useMutation, useQuery } from "@apollo/client";
import UserAvatar from "../user-avatar";
import { IPlayer } from "../../types";
import {
  YellowCardIcon,
  RedCardIcon,
  GoalIcon,
  OwnGoalBadge,
  ButtonRow,
  AwayTeamButton,
} from "./StyledElements";
import DelIcon from "@material-ui/icons/RemoveCircleOutline";
import Formation5 from "./Formation5";
import Substitutions from "./Substitutions";
import BallIcon from "@material-ui/icons/SportsSoccer";
import Formation7 from "./Formation7";
import Formation9 from "./Formation9";
import Formation11 from "./Formation11";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import RestartAltIcon from "@material-ui/icons/Replay";
import { IStyledArguments } from "../../types/styled-arguments";

const Header = styled("div")({
  color: "#ffffff",
  fontSize: "large",
  fontWeight: 700,
  textAlign: "center",
  position: "absolute",
  top: 16,
  left: 80,
  right: 140,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
  zIndex: 2,
  "@media (max-width: 600px)": {
    fontSize: "small",
    top: 12,
    left: 60,
    right: 100,
  },
});

const LogoContainer = styled("div")({
  position: "absolute",
  top: 16,
  left: 16,
  zIndex: 3,
  "& img": {
    height: "50px",
    width: "auto",
    filter: "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))",
  },
  "@media (max-width: 600px)": {
    "& img": {
      height: "40px",
    },
  },
});

const MatchInfo = styled("div")({
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  position: "absolute",
  bottom: 12,
  left: 16,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  background: "rgba(16, 21, 26, 0.45)",
  backdropFilter: "blur(10px)",
  borderRadius: "8px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  zIndex: 2,
});

// Scrim oscuro plano sobre la foto del estadio — legibilidad, no marca
const OpacityLayer = styled("div")({
  background: "rgba(16, 21, 26, 0.55)",
  height: "100%",
  width: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
});

const ScoreBox = styled("div")({
  backgroundImage: "url('/images/stadium.jpg')",
  backgroundPosition: "center",
  backgroundAttachment: "initial",
  backgroundSize: "cover",
  height: "280px",
  backgroundRepeat: "no-repeat",
  color: "white",
  top: 0,
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
  padding: 8,
  position: "relative",
  marginBottom: 16,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  overflow: "hidden",
  "@media (max-width: 600px)": {
    height: "320px",
  },
});

const ScoreWrapper = styled("div")({
  margin: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  gap: "16px",
  zIndex: 2,
});

// Marcador — misma caja para local y visitante, un único acento, sin glow
const LocalScore = styled("div")(({ theme }: IStyledArguments) => {
  return {
    fontFamily: theme.typography.alternateFontFamily,
    fontSize: "2.5rem",
    fontVariantNumeric: "tabular-nums",
    background: theme.palette.primary.main,
    borderRadius: "16px",
    width: "70px",
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "9px",
    color: "white",
    textAlign: "center",
    fontWeight: 800,
    border: "2px solid rgba(255, 255, 255, 0.3)",
  };
});

const AwayScore = styled(LocalScore)({});

const VSBox = styled("div")(() => {
  return {
    fontSize: "xx-large",
    borderRadius: "8px",
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: 700,
    textShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
  };
});
const AwayTeamControls = styled("div")(({ theme }: IStyledArguments) => {
  return {
    display: "flex",
    flexDirection: "column",
    margin: "16px",
    background: "rgba(255, 255, 255, 0.95)",
    border: `1px solid ${theme.palette.extra.borderColor}`,
    borderRadius: "16px",
    padding: "20px",
    gap: "16px",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
  };
});
const ScorerListHome = styled("div")({
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  position: "absolute",
  top: 100,
  left: 16,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "12px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.25)",
  maxWidth: "140px",
  zIndex: 2,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "@media (max-width: 600px)": {
    fontSize: "x-small",
    padding: "8px",
    maxWidth: "120px",
    top: 110,
  },
});
const ScorerListAway = styled("div")({
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  position: "absolute",
  top: 100,
  right: 16,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "12px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.25)",
  maxWidth: "140px",
  zIndex: 2,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "@media (max-width: 600px)": {
    fontSize: "x-small",
    padding: "8px",
    maxWidth: "120px",
    top: 110,
  },
});
const AdmonishedAway = styled("div")({
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  position: "absolute",
  bottom: 140,
  left: 16,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "12px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.25)",
  maxWidth: "140px",
  zIndex: 2,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "@media (max-width: 600px)": {
    fontSize: "x-small",
    padding: "8px",
    maxWidth: "120px",
    bottom: 160,
  },
});
const AdmonishedHome = styled("div")({
  color: "white",
  fontSize: "small",
  fontWeight: 600,
  position: "absolute",
  bottom: 140,
  right: 16,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "12px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.25)",
  maxWidth: "140px",
  zIndex: 2,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  "@media (max-width: 600px)": {
    fontSize: "x-small",
    padding: "8px",
    maxWidth: "120px",
    bottom: 160,
  },
});

const TimerContainer = styled("div")({
  position: "absolute",
  top: 16,
  right: 16,
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "8px",
  "@media (max-width: 600px)": {
    top: 12,
    right: 12,
    transform: "scale(0.85)",
    transformOrigin: "top right",
  },
});

const TimerDisplay = styled("div")({
  background: "rgba(255, 255, 255, 0.25)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  padding: "12px 16px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const TimerText = styled("div")({
  color: "white",
  fontSize: "24px",
  fontWeight: 700,
  fontFamily: "monospace",
  textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
});

const TimerControlButton = styled("button")({
  background: "rgba(255, 255, 255, 0.3)",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  borderRadius: "8px",
  color: "white",
  cursor: "pointer",
  padding: "6px 8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.4)",
    transform: "scale(1.05)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
});

const PartIndicator = styled("div")(({ theme }: IStyledArguments) => ({
  color: "white",
  fontSize: "12px",
  fontWeight: 600,
  background: theme.palette.primary.main,
  padding: "4px 12px",
  borderRadius: "8px",
  textAlign: "center",
}));

const MainContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  paddingBottom: "80px",
  paddingTop: "20px",
}));

const ContentWrapper = styled("div")({
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "0",
});

const MatchComponentV2 = injectIntl<any>(
  ({ matchId, callback, intl, team, lineUp }) => {
    const theme = useTheme<Theme>();
    const { data, refetch } = useQuery(QUERY_MATCH, {
      variables: {
        id: matchId,
      },
    });
    const [score, setScore] = useState({ home: 0, away: 0 });
    const [scorerList, setScorerList] = useState<{ [index: string]: number }>(
      {}
    );
    const [yellows, setYellows] = useState<{ [index: string]: number }>({});
    const [reds, setReds] = useState<{ [index: string]: number }>({});
    const [ownGoals, setOwnGoals] = useState<number>(0);
    const [endMatch, { data: dataEndtMatch }] = useMutation(END_MATCH);
    const [updateMatchEvent, { data: matchUpdated }] = useMutation(
      UPDATE_MATCH_EVENT
    );
    const [substitutions, setSubstituions] = useState();
    const [players, setPlayers] = useState<any>();
    const [alineacion, setAlineacion] = useState<any>(lineUp);
    const [match, setMatch] = useState<any>();
    
    // Timer state
    const [elapsedTime, setElapsedTime] = useState<number>(0);
    const [currentPart, setCurrentPart] = useState<number>(1);
    const [isTimerRunning, setIsTimerRunning] = useState<boolean>(true);

    useEffect(() => {
      // const selected: { [index: string]: boolean } = {};

      if (data && data.match) {
        console.log("[MATCHCOMPONENTV2], match data received", data.match);
        setMatch(data.match);
      }
    }, [data]);

    // Timer effect
    useEffect(() => {
      if (!isTimerRunning || !match?.config) return;

      const interval = setInterval(() => {
        setElapsedTime((prev) => {
          const timePerPartInSeconds = match.config.timePerPart * 60;
          const newTime = prev + 1;

          // Check if we need to move to next part
          if (newTime >= timePerPartInSeconds * currentPart && currentPart < match.config.gameParts) {
            setCurrentPart((prevPart) => prevPart + 1);
          }

          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }, [isTimerRunning, match, currentPart]);

    // Keep-alive effect to prevent session timeout during ongoing match
    useEffect(() => {
      if (!match || match.status !== 'ONGOING') return;

      // Ping server every 2 minutes to keep session alive
      const keepAliveInterval = setInterval(() => {
        console.log('[KeepAlive] Refreshing session during ongoing match');
        // Refetch match data to keep connection alive
        refetch().catch((error) => {
          console.error('[KeepAlive] Error refreshing session:', error);
        });
      }, 120000); // 2 minutes

      return () => clearInterval(keepAliveInterval);
    }, [match, refetch]);

    useEffect(() => {
      if (match && match.playersConfirmed) {
        let playersOpen: { [index: string]: boolean } = {};
        let players: { [index: string]: IPlayer } = {};
        match.playersConfirmed.forEach((player: IPlayer) => {
          playersOpen[player.id] = false;
          players[player.id] = player;
        });
        setPlayers(players);
        // console.log("players-------");
        // console.log(playersOpen);
        // setOpen(playersOpen);
        // console.log("lineUp-----");
        // console.log(alineacion);
        // console.log("lineUp-----");
        const subs: any = [];
        Object.keys(playersOpen).forEach((player: any) => {
          console.log(player);
          var isInTheLineUp = Object.values(alineacion).some(
            (p) => p === player
          );
          if (!isInTheLineUp) {
            subs.push(players[player]);
          }
        });
        setSubstituions(subs);
      }
    }, [match, alineacion]);

    useEffect(() => {
      console.log("SCORE ----> ", data.match.score);
      setScore(data.match.score);
      const mapScorerList: { [index: string]: number } = {};
      if (data.match.scorerList) {
        for (let scorerFromDB of data.match.scorerList) {
          console.log("[DEBUG]", scorerFromDB);
          console.log("[DEBUG] map", mapScorerList);
          // if (scorerFromDB) {
          const key = scorerFromDB.player.id;
          console.log("[DEBUG] key", key);
          // console.log(key);
          if (!mapScorerList[key]) {
            mapScorerList[key] = scorerFromDB.goals;
          }
          // }
        }
        console.log("[SCORER_LIST From QUERY]", mapScorerList);
        // console.log(mapScorerList);
        setScorerList(mapScorerList);
      }
    }, [players]);

    useEffect(() => {
      // handleUpadateMatchEvent();
      console.log("[SCORER_LIST]*******", scorerList);
      console.log("*******SCORE", score);
      if (scorerList || score.away > 0 || score.home > 0) {
        handleUpadateMatchEvent();
      }
    }, [scorerList, yellows, reds, ownGoals, score]);

    useEffect(() => {
      if (dataEndtMatch) {
        callback(dataEndtMatch.endMatch.status);
      }
    }, [dataEndtMatch]);

    useEffect(() => {
      if (matchUpdated) {
        refetch();
      }
    }, [matchUpdated]);

    const handleYellowCard = (playerId: string) => {
      const newYellows = { ...yellows };

      if (newYellows[playerId]) {
        newYellows[playerId] = newYellows[playerId] + 1;
      } else {
        newYellows[playerId] = 1;
      }
      if (newYellows[playerId] === 2) {
        const newReds = { ...reds };
        newReds[playerId] = 1;
        setReds(newReds);
      }
      setYellows(newYellows);
    };

    const handleRedCard = (playerId: string) => {
      const newReds = { ...reds };

      if (newReds[playerId]) {
        newReds[playerId] = newReds[playerId] + 1;
      } else {
        newReds[playerId] = 1;
      }
      setReds(newReds);
    };

    const handleScoreGoal = (playerId: string) => {
      const newScore = { ...score };
      newScore.home = score.home + 1;
      setScore(newScore);
      const newScorerList = { ...scorerList };
      if (newScorerList[playerId]) {
        newScorerList[playerId] = newScorerList[playerId] + 1;
      } else {
        newScorerList[playerId] = 1;
      }
      console.log("[SCORER_LIST] handleScoreGoal", newScorerList);
      setScorerList(newScorerList);
      // handleUpadateMatchEvent();
    };

    //TODO: dont delete this!
    const handleRemoveScoredGoal = (playerId: string) => {
      const newScore = { ...score };
      if (newScore.home > 0) {
        newScore.home = score.home - 1;
        setScore(newScore);
        const newScorerList = { ...scorerList };
        if (newScorerList[playerId] && newScorerList[playerId] >= 1) {
          newScorerList[playerId] = newScorerList[playerId] - 1;
        }
        setScorerList(newScorerList);
      }
    };

    const handleScoreAwayGoal = () => {
      const newScore = { ...score };
      newScore.away = score.away + 1;
      setScore(newScore);
    };

    const handleScoreOwnAwayGoal = () => {
      const newScore = { ...score };
      newScore.home = score.home + 1;
      setScore(newScore);
      setOwnGoals(ownGoals + 1);
    };

    const handleRemoveOwnAwayGoal = () => {
      const newScore = { ...score };
      newScore.home = score.home - 1;
      setScore(newScore);
      setOwnGoals(ownGoals - 1);
    };

    const handleRemoveAwayGoal = () => {
      const newScore = { ...score };
      newScore.away = score.away - 1;
      setScore(newScore);
    };

    // const handleClose = () => {
    //   setOpen(false);
    // };

    // const handleOpen = (id: any) => {
    //   const newOpen = { ...open };
    //   newOpen[id] = true;
    //   setOpen(newOpen);
    // };

    const handleBack = (status: string) => {
      callback(status);
    };

    const handleSubstitution = (matchEvent: any) => {
      // handleUpadateMatchEvent();
      const newAlineacion = { ...alineacion };
      console.log("[debug] handleSubstitution ", newAlineacion);
      const positionChanged = Object.keys(newAlineacion).find((position) => {
        if (newAlineacion[position] === matchEvent.playerOut) {
          return position;
        }
      });
      if (positionChanged) {
        newAlineacion[positionChanged] = matchEvent.playerIn;
        console.log(
          "[debug] handleSubstitution new alineacion",
          newAlineacion,
          positionChanged
        );
        setAlineacion(newAlineacion);
      }
    };

    const handleMatchEvent = (matchEvent: any) => {
      if (matchEvent.type === "Goal") {
        console.log("goal from ", matchEvent.player);
        handleScoreGoal(matchEvent.player);
      } else if (matchEvent.type === "yellowCard") {
        console.log("yellow from ", matchEvent.player);
        handleYellowCard(matchEvent.player);
      } else if (matchEvent.type === "removeGoal") {
        console.log("remove goal from ", matchEvent.player);
        handleRemoveScoredGoal(matchEvent.player);
      } else if (matchEvent.type === "redCard") {
        console.log("red from ", matchEvent.player);
        handleRedCard(matchEvent.player);
      } else if (matchEvent.type === "substitution") {
        console.log("substitution ", matchEvent);
        //handleRedCard(matchEvent.player);
        console.log("[debug] handleSubstitution event -> ", matchEvent);
        handleSubstitution(matchEvent);
      }
    };

    const handleEndMatch = async () => {
      console.log("endMatch");
      console.log(scorerList);
      const pushScorerList: any[] = [];
      Object.keys(scorerList).forEach(function(key) {
        console.log(key);
        console.log(scorerList[key]);
        pushScorerList.push({
          playerId: key,
          value: scorerList[key],
        });
      });
      console.log("pushScorerList");
      console.log(pushScorerList);

      const admonishedList: any[] = [];
      Object.keys(yellows).forEach((key) => {
        admonishedList.push({
          playerId: key,
          yellow: yellows[key],
          red: reds[key] || 0,
        });
      });
      Object.keys(reds).forEach((key) => {
        if (!admonishedList.some((element: any) => element.playerId === key)) {
          admonishedList.push({
            playerId: key,
            yellow: 0,
            red: reds[key],
          });
        }
      });

      // console.log("admonishedList----------");
      // console.log(admonishedList);
      await endMatch({
        variables: {
          matchId: matchId,
          scoreHome: score.home,
          scoreAway: score.away,
          scorerList: pushScorerList,
          ownGoals,
          admonisheds: admonishedList,
        },
      });
    };
    const handleUpadateMatchEvent = async () => {
      console.log("updateMatchEvent");
      console.log(scorerList);
      const pushScorerList: any[] = [];
      Object.keys(scorerList).forEach(function(key) {
        console.log(key);
        console.log(scorerList[key]);
        pushScorerList.push({
          playerId: key,
          value: scorerList[key],
        });
      });
      console.log("pushScorerList");
      console.log(pushScorerList);

      const admonishedList: any[] = [];
      Object.keys(yellows).forEach((key) => {
        admonishedList.push({
          playerId: key,
          yellow: yellows[key],
          red: reds[key] || 0,
        });
      });
      Object.keys(reds).forEach((key) => {
        if (!admonishedList.some((element: any) => element.playerId === key)) {
          admonishedList.push({
            playerId: key,
            yellow: 0,
            red: reds[key],
          });
        }
      });

      // console.log("admonishedList----------");
      // console.log(admonishedList);
      await updateMatchEvent({
        variables: {
          matchId: matchId,
          scoreHome: score.home,
          scoreAway: score.away,
          scorerList: pushScorerList,
          ownGoals,
          admonisheds: admonishedList,
        },
      });
    };

    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getCurrentPartTime = (): number => {
      if (!match?.config) return 0;
      const timePerPartInSeconds = match.config.timePerPart * 60;
      return elapsedTime % timePerPartInSeconds;
    };

    return (
      <>
        {match && players ? (
          <MainContainer>
            <ContentWrapper>
            <ScoreBox>
              <OpacityLayer />
              <LogoContainer>
                <img src="/images/footik-mark.svg" alt="Footik" />
              </LogoContainer>
              {match.config && (
                <TimerContainer>
                  <PartIndicator>
                    Part {currentPart} / {match.config.gameParts}
                  </PartIndicator>
                  <TimerDisplay>
                    <TimerText>{formatTime(getCurrentPartTime())}</TimerText>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <TimerControlButton 
                        onClick={() => setIsTimerRunning(!isTimerRunning)}
                        title={isTimerRunning ? "Pause" : "Start"}
                      >
                        {isTimerRunning ? <PauseIcon style={{ fontSize: 20 }} /> : <PlayArrowIcon style={{ fontSize: 20 }} />}
                      </TimerControlButton>
                      <TimerControlButton 
                        onClick={() => {
                          setElapsedTime(0);
                          setCurrentPart(1);
                        }}
                        title="Reset"
                      >
                        <RestartAltIcon style={{ fontSize: 20 }} />
                      </TimerControlButton>
                    </div>
                  </TimerDisplay>
                </TimerContainer>
              )}
              <Header>
                {match.isHomeTeamLocal ? (
                  <>{match.homeTeam.name}</>
                ) : (
                  <>{match.awayTeam}</>
                )}

                {" vs "}
                {match.isHomeTeamLocal ? (
                  <>{match.awayTeam}</>
                ) : (
                  <>{match.homeTeam.name}</>
                )}
              </Header>
              <MatchInfo>
                <img
                  src="/images/match/soccer-field.png"
                  width="20"
                  height="20"
                  alt="field"
                />
                <div>Match in Progress</div>
              </MatchInfo>
              <>
                {match.isHomeTeamLocal ? (
                  <>
                    <ScorerListHome>
                      {Object.keys(scorerList).map((scorer: any) => {
                        console.log("[DEBUG1] scorer", scorer);
                        console.log("[DEBUG1] players", players);
                        return (
                          <>
                            {scorerList[scorer] > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  alignItems: "center",
                                }}
                              >
                                <BallIcon />
                                {players[scorer].name} ({scorerList[scorer]})
                              </div>
                            )}
                          </>
                        );
                      })}
                    </ScorerListHome>
                    <AdmonishedHome>
                      {Object.keys(yellows).map((scorer: any) => (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                          }}
                        >
                          <YellowCardIcon />
                          {players[scorer].name}
                        </div>
                      ))}
                      {Object.keys(reds).map((scorer: any) => (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                          }}
                        >
                          <RedCardIcon />
                          {players[scorer].name}
                        </div>
                      ))}
                    </AdmonishedHome>
                  </>
                ) : (
                  <>
                    <ScorerListAway>
                      {Object.keys(scorerList).map((scorer: any) => {
                        console.log("[DEBUG2] scorer", scorer);
                        console.log("[DEBUG2] players", players);
                        console.log("[DEBUG2] scoreList", scorerList);
                        return (
                          <>
                            {scorerList[scorer] > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  alignItems: "center",
                                }}
                              >
                                <BallIcon />
                                {players[scorer].name} ({scorerList[scorer]})
                              </div>
                            )}
                          </>
                        );
                      })}
                    </ScorerListAway>
                    <AdmonishedAway>
                      {Object.keys(yellows).map((scorer: any) => (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                          }}
                        >
                          <YellowCardIcon />
                          {players[scorer].name}
                        </div>
                      ))}
                      {Object.keys(reds).map((scorer: any) => (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                          }}
                        >
                          <RedCardIcon />
                          {players[scorer].name}
                        </div>
                      ))}
                    </AdmonishedAway>
                  </>
                )}
              </>
              {/* )} */}
              <ScoreWrapper>
                <LocalScore>
                  {match.isHomeTeamLocal ? (
                    <>{score.home}</>
                  ) : (
                    <>{score.away}</>
                  )}
                </LocalScore>
                <VSBox>{"-"}</VSBox>
                <AwayScore>
                  {match.isHomeTeamLocal ? (
                    <>{score.away}</>
                  ) : (
                    <>{score.home}</>
                  )}
                </AwayScore>
              </ScoreWrapper>
            </ScoreBox>
            <div style={{ padding: "5px", position: "relative", top: "-80px" }}>
              {match && match.config && match.config.numPlayers === 5 && (
                <Formation5
                  matchId={matchId}
                  match={match}
                  lineUp={alineacion}
                  team={team}
                  subs={substitutions}
                  isMatchOngoing={true}
                  scorerList={scorerList}
                  callback={(numSelected: any) => {
                    console.log("Seleccionados::::", numSelected);
                    // setSelectedPlayers(numSelected);
                  }}
                  onMatchEvent={(matchEvent: any) =>
                    handleMatchEvent(matchEvent)
                  }
                />
              )}
              {match && match.config && match.config.numPlayers === 7 && (
                <Formation7
                  matchId={matchId}
                  match={match}
                  lineUp={alineacion}
                  team={team}
                  subs={substitutions}
                  isMatchOngoing={true}
                  scorerList={scorerList}
                  callback={(numSelected: any) => {
                    console.log("Seleccionados::::", numSelected);
                    // setSelectedPlayers(numSelected);
                  }}
                  onMatchEvent={(matchEvent: any) =>
                    handleMatchEvent(matchEvent)
                  }
                />
              )}
              {match && match.config && match.config.numPlayers === 9 && (
                <Formation9
                  matchId={matchId}
                  match={match}
                  lineUp={alineacion}
                  team={team}
                  subs={substitutions}
                  isMatchOngoing={true}
                  scorerList={scorerList}
                  callback={(numSelected: any) => {
                    console.log("Seleccionados::::", numSelected);
                    // setSelectedPlayers(numSelected);
                  }}
                  onMatchEvent={(matchEvent: any) =>
                    handleMatchEvent(matchEvent)
                  }
                />
              )}
              {match && match.config && match.config.numPlayers === 11 && (
                <Formation11
                  matchId={matchId}
                  match={match}
                  lineUp={alineacion}
                  team={team}
                  subs={substitutions}
                  isMatchOngoing={true}
                  scorerList={scorerList}
                  callback={(numSelected: any) => {
                    console.log("Seleccionados::::", numSelected);
                    // setSelectedPlayers(numSelected);
                  }}
                  onMatchEvent={(matchEvent: any) =>
                    handleMatchEvent(matchEvent)
                  }
                />
              )}
              {/* <!-- substitutions --> */}
              <Substitutions players={substitutions} />

              {/* <!-- away team goal --> */}
              <AwayTeamControls>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", width: "100%" }}>
                  <Fab
                    variant="extended"
                    color="inherit"
                    aria-label="add"
                    onClick={(_) => handleScoreAwayGoal()}
                    style={{
                      background: theme.palette.primary.main,
                      color: "white",
                      fontWeight: 600,
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <GoalIcon />
                    <AwayTeamButton>{match.awayTeam}</AwayTeamButton>
                  </Fab>
                  {score.away > 0 && (
                    <Fab
                      size="small"
                      color="inherit"
                      aria-label="add"
                      onClick={(_) => handleRemoveAwayGoal()}
                      style={{
                        background: theme.palette.error.main,
                        color: "white",
                      }}
                    >
                      <DelIcon />
                    </Fab>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", width: "100%" }}>
                  <Fab
                    variant="extended"
                    color="inherit"
                    aria-label="add"
                    onClick={(_) => handleScoreOwnAwayGoal()}
                    style={{
                      background: theme.palette.extra.loss,
                      color: "white",
                      fontWeight: 600,
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    <GoalIcon />
                    Own Goal
                  </Fab>

                  {ownGoals > 0 && (
                    <Fab
                      size="small"
                      color="inherit"
                      aria-label="add"
                      onClick={(_) =>
                        handleRemoveOwnAwayGoal()
                      }
                      style={{
                        background: theme.palette.error.main,
                        color: "white",
                      }}
                    >
                      <DelIcon />
                    </Fab>
                  )}

                  {ownGoals > 0 && (
                    <OwnGoalBadge
                      color="secondary"
                      badgeContent={ownGoals}
                      style={{ marginLeft: "auto" }}
                    >
                      <UserAvatar
                        style={{
                          backgroundColor: theme.palette.extra.loss,
                        }}
                        user={{
                          name: intl.formatMessage(messages.own),
                          surname: intl.formatMessage(messages.goal),
                        }}
                      />
                    </OwnGoalBadge>
                  )}
                </div>
              </AwayTeamControls>
            </div>

            <ButtonRow style={{ 
              marginRight: 16, 
              marginBottom: "24px",
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              padding: "0 16px",
            }}>
              <Fab
                color="secondary"
                onClick={() => handleBack("READY")}
                style={{
                  borderRadius: 32,
                  width: "56px",
                  height: "56px",
                  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.2)",
                }}
              >
                <ArrowBackIcon />
              </Fab>
              <Fab
                color="primary"
                onClick={handleEndMatch}
                variant="extended"
                style={{
                  borderRadius: 32,
                  padding: "0 32px",
                  height: "56px",
                  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.2)",
                  fontWeight: 600,
                  fontSize: "16px",
                }}
              >
                <EndGameIcon style={{ marginRight: 8 }} />
                {intl.formatMessage(messages.endGame)}
              </Fab>
            </ButtonRow>
            </ContentWrapper>
          </MainContainer>
        ) : (
          <div>Loading...</div>
        )}
      </>
    );
  }
);

export default MatchComponentV2;
